// ABOUTME: Sends a chat message and follows the answer's status until it is
// ABOUTME: final, refused, too old, or no longer this user's to follow.

import { useState, useCallback, useEffect, useRef } from "react"
import { CombinedGraphQLErrors } from "@apollo/client/errors"
import { useQuery, useMutation } from "@apollo/client/react"
import { useTranslation } from "react-i18next"
import {
  ChatSendMessageStreamDocument,
  ChatMessageStatusDocument,
  GetConversationsDocument,
  ChatProcessingStatus,
} from "@/graphql/main/gql/graphql"
import { createLogger } from "@/model/logger"
import { errorTranslationKey } from "@/graphql/client/errorMessage"
import { useNotificationContext } from "@/app/providers/useNotificationContext"
import type { PageContext } from "../types"

const logger = createLogger("useChatMessages")

// How long an answer is followed before it is given up on. The server gives a
// turn ten minutes; a status still not final after that is not going to become
// so, and the answer, if there is one, is in the conversation.
const POLLING_LIMIT_MS = 10 * 60 * 1000

interface UseChatMessagesProps {
  refetchConversations: () => Promise<unknown>
  pollingLimitMs?: number
}

export const useChatMessages = ({ refetchConversations, pollingLimitMs = POLLING_LIMIT_MS }: UseChatMessagesProps) => {
  const { t } = useTranslation()
  const { handleNotification } = useNotificationContext()
  const [messageText, setMessageText] = useState("")
  const [isSendingMessage, setIsSendingMessage] = useState(false)
  const [pendingMessage, setPendingMessage] = useState<string | null>(null)
  const [pollingMessageId, setPollingMessageId] = useState<string | null>(null)
  const [pollingConversationId, setPollingConversationId] = useState<string | null>(null)
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const [sendMessageStream] = useMutation(ChatSendMessageStreamDocument)

  // Poll message status when we have a message ID
  const shouldPoll = Boolean(pollingMessageId && pollingConversationId)
  const {
    data: messageStatus,
    error: statusError,
    stopPolling,
  } = useQuery(ChatMessageStatusDocument, {
    variables: {
      conversationId: pollingConversationId ?? "",
      messageId: pollingMessageId ?? "",
    },
    skip: !shouldPoll,
    pollInterval: shouldPoll ? 1000 : 0, // Poll every second only when active
  })
  const isFinalStatus =
    messageStatus?.chatMessageStatus.status === ChatProcessingStatus.Complete ||
    messageStatus?.chatMessageStatus.status === ChatProcessingStatus.Error
  // Complete while the followed message's status is final and its ids are still held: derived, not
  // stored, so it turns true in the render that sees the answer and false once the ids are let go.
  const isPollingComplete = shouldPoll && isFinalStatus

  // followUp numbers the follow-ups. A send that comes back after the follow-up
  // it began was dropped by a cancel must not start polling for a message
  // nobody is waiting on any more.
  const followUpRef = useRef(0)

  // stopTracking ends the follow-up of the current message, whatever state it
  // was in: nothing is polled, nothing is pending, nothing is being sent.
  const stopTracking = useCallback(() => {
    followUpRef.current += 1

    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current)
      pollingTimeoutRef.current = null
    }

    stopPolling()
    setPollingMessageId(null)
    setPollingConversationId(null)
    setIsSendingMessage(false)
    setPendingMessage(null)
  }, [stopPolling])

  // Handle status completion
  useEffect(() => {
    // Clear any existing timeout
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current)
      pollingTimeoutRef.current = null
    }

    if (isFinalStatus) {
      logger.debug("Message status complete/error, stopping polling", {
        status: messageStatus.chatMessageStatus.status,
        finalContent: messageStatus.chatMessageStatus.finalContent,
        pollingMessageId,
        pollingConversationId,
      })

      // Stop polling
      stopPolling()

      // Clear polling state after a delay to ensure UI updates
      pollingTimeoutRef.current = setTimeout(() => {
        setPollingMessageId(null)
        setPollingConversationId(null)
        setIsSendingMessage(false)
        pollingTimeoutRef.current = null
      }, 100)

      // Refetch conversations to get the final message
      refetchConversations().catch((error) => {
        logger.error("Error refetching conversations", error)
      })
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current)
        pollingTimeoutRef.current = null
      }
    }
  }, [
    isFinalStatus,
    messageStatus?.chatMessageStatus.status,
    messageStatus?.chatMessageStatus.finalContent,
    stopPolling,
    refetchConversations,
    pollingMessageId,
    pollingConversationId,
  ])

  // A GraphQL error is the server's definitive answer about this message: the
  // conversation is not this user's, or the ids are not ids. Asking again gets
  // the same answer, so the follow-up ends here. A network error is not
  // definitive, and polling rides through it as before.
  useEffect(() => {
    if (!shouldPoll || !statusError || !CombinedGraphQLErrors.is(statusError)) return

    logger.warn("Message status refused, stopping polling", {
      codes: statusError.errors.map((error) => error.extensions?.code),
      pollingMessageId,
      pollingConversationId,
    })
    stopTracking()
    handleNotification("error", t("chat.statusUnavailable"), false, 5000)
  }, [shouldPoll, statusError, stopTracking, handleNotification, t, pollingMessageId, pollingConversationId])

  // An answer that is not final after the limit is given up on; whatever came
  // of it is in the conversation, not in the status. The timer is tied to the
  // follow-up alone: what giving up does is read through a ref, so a callback
  // that changed identity on a render cannot restart the clock.
  const giveUp = useRef(() => {})
  useEffect(() => {
    giveUp.current = () => {
      logger.warn("Message status not final within the limit, stopping polling", {
        pollingMessageId,
        pollingConversationId,
      })
      stopTracking()
      handleNotification("error", t("chat.answerTimedOut"), false, 5000)
    }
  })
  useEffect(() => {
    // A finished answer keeps its ids for a moment so the UI can settle; that
    // moment is not one to give up in.
    if (!shouldPoll || isPollingComplete) return

    const timer = setTimeout(() => giveUp.current(), pollingLimitMs)

    return () => clearTimeout(timer)
  }, [shouldPoll, isPollingComplete, pollingLimitMs])

  const handleSendMessage = useCallback(
    async (conversationId: string | null, textToSend: string | null, pageContext: PageContext) => {
      const text = textToSend || messageText

      if (!text.trim() || !conversationId) {
        setIsSendingMessage(false)
        return
      }

      if (!isSendingMessage) {
        setIsSendingMessage(true)
      }

      // Save the message text in case we need to restore it on error
      const savedMessageText = messageText

      // Clear input immediately
      setMessageText("")

      // Set pending message to show it immediately
      setPendingMessage(text)

      const followUp = followUpRef.current

      try {
        // Use streaming mutation
        const { data } = await sendMessageStream({
          variables: {
            input: {
              conversationId,
              text,
              context: {
                page: {
                  ...pageContext,
                  title: pageContext.title || "",
                  description: pageContext.description || "",
                  path: pageContext.path || "",
                  hash: pageContext.hash || "",
                },
              },
            },
          },
          refetchQueries: [{ query: GetConversationsDocument }],
        })

        // The follow-up was dropped while the send was in flight: the message is
        // sent, and nobody here is waiting on its answer.
        if (followUp !== followUpRef.current) return

        // Get the ID of the message we just sent
        const messages = data?.chatSendMessageStream.messages || []
        const lastMessage = messages[messages.length - 1]

        if (lastMessage?.id) {
          logger.debug("Message sent via stream, starting polling", {
            messageId: lastMessage.id,
            conversationId,
          })

          // Set up polling
          setPollingMessageId(lastMessage.id)
          setPollingConversationId(conversationId)

          // Clear pending message as it's now being processed
          setPendingMessage(null)
        } else {
          throw new Error("Failed to get message ID from stream response")
        }
      } catch (error) {
        logger.error("Error sending message", error)
        setIsSendingMessage(false)

        // Clear pending message on error
        setPendingMessage(null)

        // Show error to user: the reason the server gave when it is one we translate (a conversation still
        // answering the previous message refuses with ErrChatTurnInProgress), the generic failure otherwise
        handleNotification("error", t(errorTranslationKey(error) ?? "chat.sendFailed"), false, 5000)

        // Restore message text on error (only if we cleared it)
        if (!textToSend && savedMessageText) {
          setMessageText(savedMessageText)
        }
      }
    },
    [messageText, isSendingMessage, sendMessageStream, handleNotification, t],
  )

  return {
    messageText,
    setMessageText,
    isSendingMessage,
    pendingMessage,
    pollingMessageId,
    messageStatus,
    isPollingComplete,
    handleSendMessage,
    handleCancelSendMessage: stopTracking,
  }
}
